import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { View } from 'react-native';
import StyleSheet from 'react-native-media-query';
import DatePicker, { useDefaultStyles } from 'react-native-ui-datepicker';
import { useDispatch, useSelector } from 'react-redux';
import {
  NavigationProp,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import { Divider, Radio } from '@ui-kitten/components';
import dayjs, { Dayjs } from 'dayjs';
import lodashFind from 'lodash/find';
import lodashGet from 'lodash/get';
import lodashIsEmpty from 'lodash/isEmpty';
import lodashIsNil from 'lodash/isNil';

import Button from '../../../Components/Button';
import DropdownPopover from '../../../Components/DropdownPopover';
import { DropdownPopoverDataItem } from '../../../Components/DropdownPopover/types';
import {
  defaultPartySizes,
  textCategory,
} from '../../../Components/Reservation/constants';
import {
  ids as reservationStyleIds,
  styles as reservationStyles,
} from '../../../Components/Reservation/styles';
import ResponsiveText from '../../../Components/ResponsiveText';
import constants from '../../../Config/constants';
import { pluralize } from '../../../Helper';
import DateHelper from '../../../Helper/Date';
import routeList, { ReservationNavigatorProps } from '../../../Routes/list';
import { useGetTableReservableDaysQuery } from '../../../RTK/api/reservations';
import { DiningServiceGroup } from '../../../RTK/api/reservations/types';
import { useGetStoreDetailsQuery } from '../../../RTK/api/store';
import { StoreHours } from '../../../RTK/api/store/types';
import { changeReservationStoreFilter } from '../../../RTK/filter';
import { selectReservationStoreFilter } from '../../../RTK/filter/selectors';
import themeStyle, { componentStyles } from '../../../Theme/styles';
import breakpoints from '../../../Theme/styles/breakpoints';
import {
  ids as platformStyleIds,
  styles as platformStyles,
} from '../../../Theme/styles/platform';
import { DBDATE, DBTIME } from '../../../types/date';

import OptionBox from './OptionBox';

const getEventDate = (date: string, range: number) => {
  return date
    ? lodashFind(constants.eventFilterData, { value: range }) ||
        constants.eventFilterData[1]
    : constants.eventFilterData[1];
};

const getTimeSlots = (date: Dayjs, hours?: StoreHours) => {
  // get opening and closing of store hours on selected date
  const selectedSchedule = lodashFind(hours, { day: date.day().toString() });
  if (lodashIsEmpty(selectedSchedule)) {
    return [{ label: '', value: '' }];
  }
  const { day, opening, closing } = selectedSchedule;
  const isToday = dayjs().day() === Number(day);
  const todayTimeNextTs = DateHelper.getToNextSlot(dayjs());
  const shouldUseTodayNextTs =
    isToday && todayTimeNextTs.isAfter(DateHelper.getDayjsAtTime(opening));
  // convert opening to dayjs for start time
  const startDayjs = DateHelper.getDayjsAtTime(
    opening,
    date.format(constants.DBDATE_FORMAT),
  );
  // convert closing to dayjs for end time
  const endDayjs = DateHelper.getDayjsAtTime(
    closing,
    date.format(constants.DBDATE_FORMAT),
  );
  return DateHelper.getTimeSlotsByPrefferedTime(
    shouldUseTodayNextTs ? todayTimeNextTs : startDayjs,
    endDayjs.add(1, 'second').subtract(2, 'hour'),
  ).map((ts) => ({
    label: ts,
    value: ts,
  }));
};

const Search = ({
  storeId,
  onSearch,
}: {
  storeId: string;
  onSearch: () => void;
}) => {
  const dispatch = useDispatch();
  const defaultStyles = useDefaultStyles();
  const navigation = useNavigation<NavigationProp<ReservationNavigatorProps>>();
  const route = useRoute();
  const filter = useSelector(selectReservationStoreFilter);

  const { data: storeInfo, isFetching: isStoreDetailsLoading } =
    useGetStoreDetailsQuery({ payload: storeId }, { skip: !storeId });

  // event state
  const [showEventDate, setShowEventDate] = useState(false);
  const [eventDate, setEventDate] = useState(
    getEventDate(filter.eventDate, filter.eventDateRange),
  );
  // table state
  const [showTableCalendar, setShowTableCalendar] = useState(false);
  const [selectedTableDate, setSelectedTableDate] = useState<Dayjs>();
  const [selectedTime, setSelectedTime] = useState<DropdownPopoverDataItem>();
  // common state
  const [selectedSize, setSelectedSize] = useState<DropdownPopoverDataItem>();

  const dateParams = lodashGet(route, 'params.date');
  const timeParams = lodashGet(route, 'params.time');
  const sizeParams = lodashGet(route, 'params.partySize');
  const eventdateRangeParams = lodashGet(route, 'params.eventDateRange');
  const isEvent = filter.tab === constants.RESERVATION_TYPE.EVENT;
  const isDining = filter.tab === constants.RESERVATION_TYPE.DINING;
  const fieldSearch = isDining
    ? lodashIsNil(selectedTableDate) || lodashIsNil(selectedTime)
    : lodashIsNil(eventDate);
  const disableSearch = fieldSearch || lodashIsNil(selectedSize);

  const {
    data: tableReservableDates,
    isFetching: isTableReservableDatesLoading,
  } = useGetTableReservableDaysQuery(
    { payload: storeId },
    { skip: !storeId && isEvent },
  );

  const timeSlots = useMemo(() => {
    if (constants.USE_DINING_SERVICE_GROUP) {
      if (lodashIsEmpty(tableReservableDates?.dining_service_group)) return [];
      return tableReservableDates.dining_service_group.map((dsg) => ({
        label: dsg.name,
        value: dsg.end_time,
      }));
    } else {
      return selectedTableDate
        ? getTimeSlots(selectedTableDate, storeInfo?.store_hours)
        : [];
    }
  }, [
    selectedTableDate,
    storeInfo?.store_hours,
    tableReservableDates?.dining_service_group,
  ]);
  const TimeTrigger = useMemo(
    () => (
      <OptionBox
        isLoading={
          constants.USE_DINING_SERVICE_GROUP && isTableReservableDatesLoading
        }
        label="Time"
        placeholder="Select Time"
        value={selectedTime}
      />
    ),
    [isTableReservableDatesLoading, selectedTime],
  );
  const PartySizeTrigger = useMemo(
    () => (
      <OptionBox
        label="Party Size"
        placeholder="Party of"
        value={selectedSize}
      />
    ),
    [selectedSize],
  );
  const _getDiningServiceGroupStartTime = useCallback(
    (updatedTime: DBTIME) => {
      if (
        !constants.USE_DINING_SERVICE_GROUP ||
        isTableReservableDatesLoading ||
        lodashIsEmpty(tableReservableDates?.dining_service_group)
      ) {
        return {
          name: DateHelper.getDayjsAtTime(updatedTime).format(
            constants.TIME_ONLY_DISPLAY_CAPITAL_FORMAT,
          ),
          start_time: updatedTime,
          end_time: undefined,
        };
      }
      const selectedTimeInBetween = lodashFind(
        tableReservableDates?.dining_service_group,
        (dsg) =>
          DateHelper.getDayjsAtTime(updatedTime).isBetween(
            DateHelper.getDayjsAtTime(dsg.start_time),
            DateHelper.getDayjsAtTime(dsg.end_time),
            null,
            '[]',
          ),
      );
      return (
        selectedTimeInBetween ?? tableReservableDates?.dining_service_group[0]
      );
    },
    [isTableReservableDatesLoading, tableReservableDates?.dining_service_group],
  );

  // effect for synching of the url parameter to redux state for store filter base on
  // store hours (storeInfo.store_hours) or dining service group (tableReservableDates.dining_service_group)
  useEffect(() => {
    if (!isStoreDetailsLoading && !isTableReservableDatesLoading) {
      const dateParamsDayjs = dayjs(dateParams).add(
        !dateParams || dateParams === dayjs().format(constants.DBDATE_FORMAT)
          ? 1
          : 0,
        'day',
      ); // convert sanitized date to dayjs
      // get time slots of the date on url parameter
      const filterDateTimeSlots = getTimeSlots(
        dateParamsDayjs,
        storeInfo?.store_hours,
      );
      // check if date on url parameter match on the updated date after checking the timeslots for it
      const isFilterDateAndUpdatedDateSame = dateParamsDayjs.isSame(
        dateParamsDayjs,
        'day',
      );
      const updatedTimeSlots = isFilterDateAndUpdatedDateSame // if no modification change selected date base on url parameter
        ? filterDateTimeSlots // use the proceesed time slots of the selected date on url parameter
        : getTimeSlots(dateParamsDayjs, storeInfo?.store_hours); // get new timeslots for the updated date (next day)
      const updatedTime = isFilterDateAndUpdatedDateSame // if no modification change selected date base on url parameter
        ? timeParams ?? '00:00' // use selected time on url parameter
        : dayjs(
            updatedTimeSlots[0].value,
            constants.TIME_ONLY_DISPLAY_CAPITAL_FORMAT,
          ).format(constants.DBTIME_FORMAT); // get the first of udpated timeslots for the next day
      // find the dining service group whose window contains updatedTime (or a fallback)
      const dsg = _getDiningServiceGroupStartTime(updatedTime as DBTIME);
      // construct payload
      const payload = {
        eventDate: eventdateRangeParams
          ? dayjs()
              .add(Number(eventdateRangeParams), 'day')
              .format(constants.DBDATE_FORMAT)
          : undefined,
        eventDateRange: eventdateRangeParams
          ? Number(eventdateRangeParams)
          : undefined,
        tableDate: dateParamsDayjs.format(constants.DBDATE_FORMAT),
        tableTime: dsg?.start_time,
        tableServingTime: dsg?.end_time,
        partySize: sizeParams ? Number(sizeParams) : 2,
      };
      const payloadNoUndefined = Object.fromEntries(
        Object.entries(payload).filter(([_, v]) => v !== undefined),
      );
      // if has 1 key it will be 'tableDate' and 'partySize', its consider as no default value since 'tableDate' always return something
      const noDefaultValues = Object.keys(payloadNoUndefined).length <= 2;
      if (!noDefaultValues) {
        dispatch(changeReservationStoreFilter(payload));
        // update local state
        if (constants.USE_DINING_SERVICE_GROUP) {
          setSelectedTime({
            label: dsg?.name,
            value: dsg?.end_time,
          });
        } else {
          const displayTime = DateHelper.getDayjsAtTime(
            payload.tableTime,
          ).format(constants.TIME_ONLY_DISPLAY_CAPITAL_FORMAT);
          setSelectedTime({ label: displayTime, value: displayTime });
        }
        setSelectedTableDate(dayjs(payload.tableDate));
        setEventDate(getEventDate(payload.eventDate, payload.eventDateRange));
        setSelectedSize({
          label: `${payload.partySize} ${pluralize(
            payload.partySize,
            'guest',
          )}`,
          value: payload.partySize,
        });
      }
    }
  }, [
    dateParams,
    eventdateRangeParams,
    isStoreDetailsLoading,
    isTableReservableDatesLoading,
    sizeParams,
    storeInfo?.store_hours,
    tableReservableDates?.dining_service_group,
    timeParams,
    dispatch,
    _getDiningServiceGroupStartTime,
  ]);

  const _onEventDateChange = (data) => {
    setEventDate(data);
    setShowEventDate(false);
  };

  const _getEnabledDates = useCallback(
    (date: Dayjs) =>
      !tableReservableDates?.blockout_dates?.includes(
        date.format(constants.DBDATE_FORMAT) as DBDATE,
      ),
    [tableReservableDates?.blockout_dates],
  );

  const _onSearch = useCallback(() => {
    // no need to dispatch here, only update url parameter
    // since this component effect handle the sync of url parameter to redux state
    const updatedUrlParams = {
      partySize: selectedSize?.value,
    } as ReservationNavigatorProps[routeList.RES_STORE_DETAILS];
    if (isEvent) {
      updatedUrlParams.eventDateRange = eventDate.value;
    } else {
      // const definedTimeBaseOnMealTime =
      updatedUrlParams.date = dayjs(selectedTableDate).format(
        constants.DBDATE_FORMAT,
      );
      if (constants.USE_DINING_SERVICE_GROUP) {
        updatedUrlParams.time = _getDiningServiceGroupStartTime(
          selectedTime?.value as DBTIME,
        )?.start_time;
      } else {
        updatedUrlParams.time = dayjs(
          selectedTime?.value,
          constants.TIME_ONLY_DISPLAY_CAPITAL_FORMAT,
        ).format(constants.DBTIME_FORMAT);
      }
    }
    onSearch?.();
    navigation.setParams(updatedUrlParams);
  }, [
    eventDate,
    isEvent,
    navigation,
    selectedSize,
    selectedTableDate,
    selectedTime,
    _getDiningServiceGroupStartTime,
    onSearch,
  ]);

  const _renderTableDateSelection = useMemo(
    () => (
      <DatePicker
        date={selectedTableDate}
        enabledDates={_getEnabledDates}
        minDate={dayjs().add(1, 'day')} // should not be able to book same day
        maxDate={dayjs().add(tableReservableDates?.max_reservable_days, 'days')}
        mode="single"
        styles={{
          ...defaultStyles,
          ...componentStyles.eventDatepicker,
        }}
        onChange={({ date }) => {
          setShowTableCalendar(false);
          setSelectedTableDate(dayjs(date));
        }}
        showOutsideDays
      />
    ),
    [
      defaultStyles,
      selectedTableDate,
      tableReservableDates?.max_reservable_days,
      _getEnabledDates,
    ],
  );

  const _renderDatePicker = useMemo(() => {
    const value = isEvent
      ? eventDate
      : selectedTableDate
      ? {
          label: selectedTableDate.format(constants.DATE_DISPLAY_FORMAT2),
          value: selectedTableDate,
        }
      : undefined;
    const updateState = isEvent ? setShowEventDate : setShowTableCalendar;
    return (
      <View style={themeStyle.flex1}>
        <OptionBox
          label="Date"
          isLoading={isTableReservableDatesLoading}
          placeholder="Select Date"
          value={value}
          onPress={() => updateState((prev) => !prev)}
        />
        {/* Event Date Selection */}
        {showEventDate &&
          constants.eventFilterData.map((d) => (
            <Fragment key={d.label}>
              <Radio
                checked={d.label === eventDate.label}
                status="info"
                onChange={() => _onEventDateChange(d)}
                style={themeStyle.pageVerticalSpacingMedium}
              >
                <ResponsiveText
                  category={textCategory.BODY}
                  style={themeStyle.capitalize}
                >
                  {d.label}
                </ResponsiveText>
              </Radio>
              <Divider style={themeStyle.divider} />
            </Fragment>
          ))}
        {/* Table Date Selection */}
        {showTableCalendar && (
          <View
            dataSet={{ media: platformStyleIds.webOnly }}
            style={[themeStyle.flex1, platformStyles.webOnly]}
          >
            {_renderTableDateSelection}
          </View>
        )}
      </View>
    );
  }, [
    eventDate,
    isEvent,
    isTableReservableDatesLoading,
    selectedTableDate,
    showEventDate,
    showTableCalendar,
    _renderTableDateSelection,
  ]);

  return (
    <View
      dataSet={{ media: reservationStyleIds.box }}
      style={reservationStyles.box}
    >
      {showTableCalendar && (
        <View
          dataSet={{
            media: platformStyleIds.mobileAndTabletOnly,
          }}
          style={[
            platformStyles.mobileAndTabletOnly,
            themeStyle.spacingBottomMedium,
            { maxWidth: 500, alignSelf: 'center' },
          ]}
        >
          {_renderTableDateSelection}
        </View>
      )}
      <View style={themeStyle.flex1}>
        <View
          dataSet={{ media: ids.searchContainer }}
          style={styles.searchContainer}
        >
          <View
            dataSet={{ media: ids.searchRowContainer }}
            style={styles.searchRowContainer}
          >
            {_renderDatePicker}
            {isDining && (
              <View
                dataSet={{ media: ids.leftSpacing }}
                style={styles.leftSpacing}
              >
                <DropdownPopover
                  data={timeSlots}
                  emptyText="No available"
                  selectionType="color"
                  trigger={TimeTrigger}
                  value={selectedTime}
                  onSelect={setSelectedTime}
                />
              </View>
            )}
          </View>

          <View
            dataSet={{ media: ids.searchRowContainer }}
            style={styles.searchRowContainer}
          >
            <View
              dataSet={{
                media: `${ids.leftSpacing} ${ids.leftSpacingPartySize}`,
              }}
              style={[styles.leftSpacing, styles.leftSpacingPartySize]}
            >
              <DropdownPopover
                trigger={PartySizeTrigger}
                value={selectedSize}
                data={defaultPartySizes}
                selectionType="color"
                onSelect={setSelectedSize}
              />
            </View>
            <View
              dataSet={{ media: ids.leftSpacing }}
              style={styles.leftSpacing}
            >
              <Button
                disabled={disableSearch}
                status="info"
                style={[
                  themeStyle.pageVerticalSpacingMedium,
                  themeStyle.noMinHeight,
                ]}
                onPress={_onSearch}
              >
                {() => (
                  <ResponsiveText
                    category={textCategory.BODY}
                    appearance="alternative"
                  >
                    Search
                  </ResponsiveText>
                )}
              </Button>
            </View>
          </View>
        </View>
      </View>
    </View>
  );
};

const { ids, styles } = StyleSheet.create({
  searchContainer: {
    [`@media ${breakpoints.sm}`]: {
      ...themeStyle.flexDirectionRow,
    },
    [`@media ${breakpoints.lg}`]: {
      ...themeStyle.flexDirectionCol,
    },
  },
  searchRowContainer: {
    ...themeStyle.flex2,
    ...themeStyle.flexDirectionRow,
    ...themeStyle.alignItemsEnd,
    [`@media ${breakpoints.xs}`]: {
      ...themeStyle.alignItemsEnd,
    },
    [`@media ${breakpoints.sm}`]: {
      ...themeStyle.alignItemsCenter,
    },
    [`@media ${breakpoints.lg}`]: {
      ...themeStyle.flexDirectionCol,
      alignItems: 'stretch',
    },
  },
  leftSpacing: {
    ...themeStyle.flex1,
    ...themeStyle.spacingLeftMedium,
    [`@media ${breakpoints.lg}`]: {
      marginLeft: 0,
    },
  },
  leftSpacingPartySize: {
    [`@media ${breakpoints.xs}`]: {
      marginLeft: 0,
    },
    [`@media ${breakpoints.lg}`]: {
      ...themeStyle.spacingBottomMedium,
    },
  },
});

export default Search;
